import { JGAMap, JgaOptions } from 'packages/ui/src/libs/Map';
import { Feature, Map as OlMap, View } from 'ol';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Text } from 'ol/style';
import GeoJSON, { GeoJSONFeature } from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import WKT from 'ol/format/WKT.js';
import VectorImageLayer from 'ol/layer/VectorImage';
import { asArray } from 'ol/color';
import { Vector, XYZ } from 'ol/source';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import { Layer } from 'ol/layer';
import Static from 'ol/source/ImageStatic';
import { Projection } from 'ol/proj';
import { EnvType } from '@metadata';
import env from './config/env';
import EtcUtils from 'packages/ui/src/libs/EtcUtils';


const LAYER_Z_INDEX = new Map<
  | 'useFarm'
  | 'aiFeature'
  | '경관보전지구'
  | 'untitle'
  | 'clickBorder',
  number
>();
LAYER_Z_INDEX.set('useFarm', 10);
LAYER_Z_INDEX.set('aiFeature', 101);
LAYER_Z_INDEX.set('경관보전지구', 8);
LAYER_Z_INDEX.set('untitle', 100);
LAYER_Z_INDEX.set('clickBorder', 200);

const ENV: EnvType = env() as EnvType;
const API_URL = `${ENV.BACKEND_API}`;


class CropMap extends JGAMap {
  private previousFeature = null;
  private previousFeatureStyle = null;
  private highlightFeature: Feature = null;
  private readonly RESOLUTION = 13; // 이 이하로 줌인할 경우 ff

  constructor(options: JgaOptions) {
    super(options);
    super.init();
  }

  init(): void {
    const currentMap = super.getMap();
    currentMap.on('pointermove', (evt) => {
      if (evt.dragging) {
        return;
      }

      const pixel = currentMap.getEventPixel(evt.originalEvent);
      this.highlighting(pixel);
    });

    currentMap.on('click', (evt) => {
      if (evt.dragging) {
        return;
      }

      const pixel = currentMap.getEventPixel(evt.originalEvent);
      this.click(pixel);
    });

    const aiLayer = new VectorImageLayer({
      // ai 분석 결과
      source: new VectorSource(),
      // maxResolution: this.RESOLUTION,
      properties: {
        tilename: 'aiFeature', // 이것으로 Map(구조체)에서 찾음.
        showCheckBox: true, // 이것으로 지도 레이어 설정의 체크박스 표출 결정
        alias: 'AI 분석 결과', // 이것으로 지도 레이어 설정의 체크박스 이름을 설정
        defaultCheckValue: true, // 체크박스 defaut
      },
      zIndex: LAYER_Z_INDEX.get('aiFeature'),
      style(feature) {
        const feat = feature.getProperties();
        const Color = asArray(`#${feat.color}`);
        Color[3] = 0.6;

        const style = new Style({
          zIndex: LAYER_Z_INDEX.get('aiFeature'),
          fill: new Fill({
            color: Color,
          }),
        });

        if (feat.farm_map_id) {
          style.setText(
            new Text({
              font: 'bold 10px Pretendard',
              text: `${feat.ai_cltv_nm}\n(${Number(feat.ai_acurcy).toFixed(1)}%)`,
              fill: new Fill({
                color: 'rgb(75,73,11)',
              }),
              stroke: new Stroke({
                color: 'rgba(255,255,255,1)',
                width: 2,
              }),
            }),
          );
        }

        return style;
      },
    });



    const featureOverlay = new VectorLayer({
      source: new VectorSource(),
      properties: {
        tilename: 'featureOverlay',
      },
      zIndex: LAYER_Z_INDEX.get('clickBorder'),
      style: {
        'stroke-color': 'rgba(255, 255, 255)',
        'stroke-width': 2,
      },
    });


    super.addLayerWithJga(aiLayer);
    // super.addLayerWithJga(untitleLayer);
    super.addLayerWithJga(featureOverlay);
   

  }

  click(pixel) {
    const feature = this.getMap().forEachFeatureAtPixel(pixel, function (feature) {
      return feature;
    });

    if (feature) {
      const feat = feature.getProperties();
      if (!feat.farm_map_id) {
        if (feat.det_id && feat.file_path_nm && feat.geometry) {
          EtcUtils.observer.mapInteraction.clickCallback(feat);
        } else return;
      } else EtcUtils.observer.mapInteraction.clickCallback(feat);
    }
  }



  highlighting(pixel) {
    const featureOverlay = this.jgaLayers.get('featureOverlay');
    if (this.highlightFeature) {
      featureOverlay.getSource().removeFeature(this.highlightFeature);
      this.getMap().getTargetElement().style.cursor = '';
    }

    this.getMap().forEachFeatureAtPixel(pixel, (feature: Feature) => {
      const props = feature.getProperties();
      if (this.isFarmmap(props.farm_map_id)) {
        this.highlightFeature = feature;
        this.getMap().getTargetElement().style.cursor = 'pointer';
        featureOverlay.getSource().addFeature(feature);
      } else if (props.obj_id && props.tree_kind_cd && props.geometry) {
        this.highlightFeature = feature;
        this.getMap().getTargetElement().style.cursor = 'pointer';
        featureOverlay.getSource().addFeature(feature);
      }
      return true;
    });
  }

  isFarmmap(id: string): boolean {
    if (!id) return false;
    return true;
  }

  untitleStyle(feature) {
    // console.log(feature.getProperties());

    const props = feature.getProperties();
    if (props.color) {
      const Color = asArray(`#${props.color}`);
      Color[3] = 0.8;

      const obj = new Style({
        fill: new Fill({
          color: Color,
        }),
        text: new Text({
          font: 'bold 6px Pretendard',
          text: props.cltv_nm,
          fill: new Fill({
            color: 'rgb(75,73,11)',
          }),
          stroke: new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 2,
          }),
        }),
      });

      return obj;
    } else if (!props.color) {
      const obj = new Style({
        fill: new Fill({
          color: 'red',
        }),
      });

      return obj;
    }
  }

  untitleLayer() {
    const vectorSource = new Vector({
      format: new GeoJSON(),
      loader: function (extent, resolution, projection, success, failure) {
        const url = `${API_URL}/crops/map/layer/all`;
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('content-type', 'application/json');
        const onError = function () {
          vectorSource.removeLoadedExtent(extent);
          failure();
        };
        xhr.onerror = onError;
        xhr.onload = function () {
          if (xhr.status === 200) {
            const parse = JSON.parse(xhr.responseText);
            const features: any = vectorSource.getFormat().readFeatures(parse.data[0]);
            vectorSource.addFeatures(features);
            success(features);
          } else {
            onError();
          }
        };

        const data = {
          year: '2023',
        };
        xhr.send(JSON.stringify(data));
      },
    });

    return vectorSource;
  }

  clearAiFeature() {
    const layer = this.jgaLayers.get('aiFeature') as VectorLayer<VectorSource>;
    const aiSource = layer.getSource();
    aiSource.clear();

    console.log('clearAiFeature');
  }

  async drawAiFeatures(geojson: GeoJSONFeature) {
    const layer = this.jgaLayers.get('aiFeature') as VectorLayer<VectorSource>;
    const aiSource = layer.getSource();

    if (!geojson) return;

    const parse = JSON.parse(geojson);

    if (!parse.features) {
      aiSource.clear();
      return;
    }

    const features: any = new GeoJSON().readFeatures(parse);

    aiSource.clear();
    aiSource.addFeatures(features);
    // console.log('drawAiFeature');
  }


  clearAiMap() {
  const layer = this.jgaLayers.get('aiFeature') as VectorLayer<VectorSource>;
    const aiSource = layer.getSource();
    aiSource.clear();
  }


  onlyMoveByFarmId(farmId: string): boolean {
    const zoom = this.getView().getZoom();
    const layer = this.jgaLayers.get('aiFeature');
    const aiSource = layer.getSource();
    const features: Array<Feature> = aiSource.getFeatures();
    if (features.length > 0) {
      for (let i = 0; i < features.length; i++) {
        const value = features[i].getProperties();
        if (value.farm_map_id === farmId) {
          const number = features[i].getGeometry().getExtent();
          if (zoom >= 18) super.moveTo({ location: number });
          else super.flyTo({ location: number });

          break;
        }
      }
      return true;
    }
    return false;
  }

  onlyDrawStroke(objId: string): boolean {
    const layer = this.jgaLayers.get('aiFeature');

    const aiSource = layer.getSource();
    const features: Array<Feature> = aiSource.getFeatures();
    if (features.length > 0) {
      for (let i = 0; i < features.length; i++) {       
        const value = features[i].getProperties();

        if (String(value.obj_id) === String(objId)) {

          if (this.previousFeature) {
            this.previousFeature.setStyle(this.previousFeatureStyle);
          }
          this.previousFeature = features[i];
          this.previousFeatureStyle = features[i].getStyle();

          const number = features[i].getGeometry().getExtent();
          super.flyTo({ location: number });

          features[i].setStyle(
            new Style({
              zIndex: LAYER_Z_INDEX.get('clickBorder'),
              stroke: new Stroke({
                lineDash: [10, 10],
                width: 7,
                color: 'red',
              }),
              fill: new Fill({
                color: `#${value.color}`,
              }),
              text: new Text({
                font: 'bold 10px Pretendard',
                text: `${value.tree_kind_nm}\n(${Number(value.area).toFixed(1)}㎡)`,
                fill: new Fill({
                  color: 'rgb(75,73,11)',
                }),
                stroke: new Stroke({
                  color: 'rgba(255,255,255,1)',
                  width: 2,
                }),
              }),
            }),
          );

          break;
        }
      }
      return true;
    }
    return false;
  }

  toMoveByFarmId(farmId: string): boolean {
    const layer = this.jgaLayers.get('aiFeature');
    const aiSource = layer.getSource();
    const features: Array<Feature> = aiSource.getFeatures();
    if (features.length > 0) {
      // features.map((feat: Feature) => {
      for (let i = 0; i < features.length; i++) {
        const value = features[i].getProperties();
        if (value.farm_map_id === farmId) {
          const number = features[i].getGeometry().getExtent();
          if (this.previousFeature) {
            this.previousFeature.setStyle(this.previousFeatureStyle);
          }
          this.previousFeature = features[i];
          this.previousFeatureStyle = features[i].getStyle();

          features[i].setStyle(
            new Style({
              zIndex: LAYER_Z_INDEX.get('clickBorder'),
              stroke: new Stroke({
                lineDash: [10, 10],
                width: 7,
                color: 'red',
              }),
              fill: new Fill({
                color: `#${value.color}`,
              }),
              text: new Text({
                font: 'bold 10px Pretendard',
                text: `${value.ai_cltv_nm}\n(${Number(value.ai_acurcy).toFixed(1)}%)`,
                fill: new Fill({
                  color: 'rgb(75,73,11)',
                }),
                stroke: new Stroke({
                  color: 'rgba(255,255,255,1)',
                  width: 2,
                }),
              }),
            }),
          );
          super.flyTo({ location: number });

          break;
        }
      }
      return true;
    }
    return false;
  }

  moveToDetected(wkt) {
    const format = new WKT();
    const feature = format.readFeature(wkt);
    const vector = new VectorImageLayer({
      source: new VectorSource({
        features: [feature],
      }),
      properties: { tilename: 'boundary' },
      zIndex: 9999,
    });

    if (this.previousFeature) {
      const obj = this.previousFeature.getStyle();

      this.previousFeature.setStyle(new Style(obj));
    }
    this.previousFeature = feature;
    feature.setStyle(
      new Style({
        stroke: new Stroke({
          lineDash: [10, 10],
          width: 7,
          color: 'red',
        }),
        // fill: new Fill({
        //   color: 'rgb(0, 0, 255, 0.6)',
        // }),
      }),
    );

    const coord = feature.getGeometry().getExtent();
    super.addLayerWithJga(vector);
    super.flyTo({ location: coord });
  }

  moveToDetectedEmpty(wkt: string) {
    const format = new WKT();
    const feature = format.readFeature(wkt);
    const vector = new VectorImageLayer({
      source: new VectorSource({
        features: [feature],
      }),
      zIndex: 9999,
      properties: { tilename: 'sample' },
    });

    super.removeLayer('sample');

    if (this.previousFeature) {
      const obj = this.previousFeature.getStyle();

      this.previousFeature.setStyle(new Style(obj));
    }
    this.previousFeature = feature;
    feature.setStyle(
      new Style({
        stroke: new Stroke({
          width: 5,
          color: 'yellow',
        }),
      }),
    );

    const coord = feature.getGeometry().getExtent();
    super.addLayerWithJga(vector);
    super.flyToFast({ location: coord, zoom: 16 });
  }

  addTileLayer(layerUrl: string, tile_ext?: string) {
    if (layerUrl) {
      try {
        super.removeLayer('tillage');

        const ext = tile_ext ? tile_ext : 'jpg';
        const tileLayer = new TileLayer({
          source: new XYZ({
            url: `${layerUrl}/{z}/{x}/{y}.${ext}`,
          }),
          properties: { tilename: 'tillage' },
        });

        super.addLayerWithJga(tileLayer);
      } catch (e) {
        console.log(e);
      }
    }
  }

  BackgroundOn() {
    const base = this.jgaLayers.get('base');
    base.setVisible(true);
  }

  addJpgLayer(layerUrl: string) {
    if (layerUrl) {
      try {
        const extent = [0, 0, 1024, 968];
        const projection = new Projection({
          code: 'xkcd-image',
          units: 'pixels',
          extent: extent,
        });

        const layer = new ImageLayer({
          source: new Static({
            url: layerUrl,
            projection: super.getView().getProjection(),
            imageExtent: [920069.3729, 1493832.7737, 939341.1693, 1511668.4694],
          }),
        });

        const base = this.jgaLayers.get('base');
        base.setVisible(true);

        super.addLayerWithJga(layer);
      } catch (e) {
        console.log(e);
      }
    }
  }
}

export default CropMap;
